import { useId, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { ERROR } from "types/snackBar.constant";
import { CSS_VARIABLES } from "enums/cssVariables";
import { getCurrentUser, performSignature } from "components/helper/callApi";
import { getAdditionalFields } from "services/additionalFields";
import { getSignatureName } from "services/signatureName";
import { useUser } from "components/helper/userContext";
import { useSignaturePad } from "hooks/useSignaturePad";
import { useDocumentTitle } from "hooks/useDocumentTitle";
import { useTypedSignature } from "hooks/useTypedSignature";
import { SECONDARY_TEXT_COLOR } from "components/common/textColors";
import { useHandleErrors } from "./common/handleErrors";
import ErrorPage from "../common/ErrorPage";
import {
    DisplayedError,
    HTTPResponseError,
    NotFoundError,
    UnknownError,
} from "services/errors";
import Layout from "../common/Layout";
import Spinner from "components/common/Spinner";
import LogoutWrapper from "components/common/LogoutWrapper";
import SignatureLogoutButton from "./common/SignatureLogoutButton";
import TypedNameField from "./common/TypedNameField";
import ProxiesAccordion from "../vote/Votebox/common/AutomaticVoteForProxiesComponents/ProxiesAccordion";

import type { User } from "types/User";

const maxWidth = 400;
const height = 200;

const Signature = () => {
    const { t } = useTranslation();
    const { user, setUser } = useUser();
    const { enqueueSnackbar } = useSnackbar();
    const { handleLoginError } = useHandleErrors();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<DisplayedError | null>(null);
    const instructionId = useId();

    useDocumentTitle(error ? null : t("pageTitle.signature"));

    const {
        canvasRef,
        underLayerCanvasRef,
        isEmpty,
        clear,
        isSignatureDrawn,
        getSignatureBlob,
        writeTypedName,
        resumeDrawing,
    } = useSignaturePad();

    const {
        clearName,
        handleNameChange,
        hasNameError,
        isTyping,
        nameInputRef,
        startTyping,
        stopTyping,
        typedName,
        validateName,
    } = useTypedSignature({
        resumeDrawing,
        signatureName: getSignatureName(user),
        writeTypedName,
    });

    async function handleValidate() {
        if (isSubmitting) {
            return;
        }

        if (isTyping && !validateName()) {
            return;
        }

        // A mobile resize can wipe the canvas after the last stroke
        if (!isTyping && !isSignatureDrawn()) {
            enqueueSnackbar(t("signature.emptyOrTooSmall"), {
                variant: ERROR,
            });
            return;
        }

        setIsSubmitting(true);
        try {
            if (isTyping) {
                // The font may still be loading: make sure the name is in the canvas
                await writeTypedName(typedName);
            }

            const blob = await getSignatureBlob();

            if (!blob) {
                enqueueSnackbar(t("errorUnknown"), { variant: ERROR });
                return;
            }

            await sendSignature(blob);
        } finally {
            setIsSubmitting(false);
        }
    }

    async function sendSignature(blob: Blob) {
        let signedUser: User | null;

        try {
            signedUser = await performSignature(blob);
        } catch (error) {
            handleSendError(error);
            return;
        }

        // `PUT /user/me/sign` may omit isSigned, the field the guard checks
        if (signedUser?.isSigned === undefined) {
            try {
                signedUser = await getCurrentUser(getAdditionalFields({}));
            } catch {
                // The signature is stored: only the refresh failed
                setUser((oldUser) =>
                    oldUser ? { ...oldUser, isSigned: true } : oldUser,
                );
                return;
            }
        }

        // Accepted but still unsigned: the guard would show a mute pad
        if (!signedUser?.isSigned) {
            enqueueSnackbar(t("errorUnknown"), { variant: ERROR });
            return;
        }

        setUser(signedUser);
    }

    function handleSendError(error: unknown) {
        if (!(error instanceof HTTPResponseError)) {
            setError(error as NotFoundError | UnknownError);
            return;
        }

        // Signature refused by the backend: blank or unreadable image
        if (error.response.status === 400) {
            enqueueSnackbar(t("signature.emptyOrTooSmall"), {
                variant: ERROR,
            });
            return;
        }

        // handleLoginError throws: without this the rejection escapes unnoticed
        try {
            handleLoginError(error.response.status);
        } catch (displayedError) {
            setError(displayedError as NotFoundError | UnknownError);
        }
    }

    const hasProxies = Boolean(user && user.proxies.length > 0);
    const handleClear = isTyping ? clearName : clear;
    const handleToggleTyping = isTyping ? stopTyping : startTyping;
    const isClearDisabled = isTyping ? !typedName : isEmpty;
    // Typing: Validate stays enabled so an empty name gets an explicit error
    const isValidateDisabled = !isTyping && isEmpty;
    const toggleTypingLabel = isTyping
        ? t("signature.backToDrawing")
        : t("signature.signWithoutDrawing");
    const padDescriptionId = isTyping ? undefined : instructionId;
    const typedNameField = isTyping ? (
        <TypedNameField
            handleChange={handleNameChange}
            hasError={hasNameError}
            inputRef={nameInputRef}
            value={typedName}
        />
    ) : null;
    const displayName = user?.displayName ? (
        <>
            {" "}
            <span>{user.displayName}</span>
        </>
    ) : null;
    // Real text, not drawn in the canvas (RGAA 1.8), between the sheet and the pen layer
    // Read once, as the pad's description, not again as page content
    const instruction = isTyping ? null : (
        <Instruction id={instructionId} aria-hidden="true">
            <span>{t("signature.signHere")}</span>
            {displayName}
        </Instruction>
    );

    if (error) {
        return <ErrorPage error={error} />;
    }

    return (
        <>
            <LogoutWrapper>
                <SignatureLogoutButton />
            </LogoutWrapper>
            <Layout>
                <>
                    <Wrapper>
                        {typedNameField}
                        <StyledCanvasWrapper>
                            <StyledCanvas
                                ref={canvasRef}
                                style={{ zIndex: "1" }}
                                role="img"
                                aria-label={t("signature.area")}
                                aria-describedby={padDescriptionId}
                            />
                            <StyledCanvas
                                ref={underLayerCanvasRef}
                                style={{ zIndex: "0", border: "none" }}
                                aria-hidden="true"
                            />
                            {instruction}
                        </StyledCanvasWrapper>
                        <ButtonWrapper>
                            <StyledOutlineButton
                                onClick={handleClear}
                                aria-label={t("clear")}
                                disabled={isClearDisabled || isSubmitting}
                            >
                                {t("clear")}
                            </StyledOutlineButton>
                            <StyledButton
                                onClick={handleValidate}
                                disabled={isValidateDisabled || isSubmitting}
                                aria-label={t("validate")}
                            >
                                {t("validate")}
                            </StyledButton>
                        </ButtonWrapper>
                        <ToggleTypingButton
                            onClick={handleToggleTyping}
                            disabled={isSubmitting}
                        >
                            {toggleTypingLabel}
                        </ToggleTypingButton>
                    </Wrapper>
                    {hasProxies && (
                        <ProxiesAccordion>
                            <StyledWrapperProxies>
                                {user?.proxies.map((proxy) => {
                                    return (
                                        <div key={proxy.id}>
                                            <p>{proxy.displayName}</p>
                                        </div>
                                    );
                                })}
                            </StyledWrapperProxies>
                        </ProxiesAccordion>
                    )}
                </>
            </Layout>
            {/* Overlaid, not an early return, so the canvas stays mounted */}
            {isSubmitting && (
                <SpinnerOverlay>
                    <Spinner label={t("loading")} />
                </SpinnerOverlay>
            )}
        </>
    );
};

const Wrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    margin: auto;
    max-width: ${maxWidth}px;
    > p {
        margin: auto;
        padding: 2rem 0 1rem 0;
        font-size: 0.75rem;
        line-height: 22px;
        text-align: left;
        width: 620px;
        > a {
            text-decoration: underline;
            color: black;
        }
        @media (max-width: 768px) {
            width: 320px;
        }
    }
`;

const ButtonWrapper = styled.div`
    display: flex;
    width: 100%;
    justify-content: end;
`;

const StyledWrapperProxies = styled.div`
    padding-left: 1.5rem;
`;

const StyledCanvasWrapper = styled.div`
    position: relative;
    width: 100%;
    height: ${height}px;
`;

const Instruction = styled.div`
    position: absolute;
    top: calc(50% - 15px);
    left: 0;
    right: 0;
    z-index: 0;
    display: flex;
    flex-direction: column;
    font:
        italic 20px/30px "Helvetica Neue",
        Arial,
        sans-serif;
    color: ${SECONDARY_TEXT_COLOR};
    padding: 0 0.5rem;
    text-align: center;
    overflow-wrap: anywhere;
    pointer-events: none;
    user-select: none;
`;

const StyledCanvas = styled.canvas`
    border: 1px solid gray;
    cursor: crosshair;
    position: absolute;
    height: 100%;
    width: 100%;
`;

const ToggleTypingButton = styled.button.attrs({ type: "button" })`
    align-self: flex-start;
    min-height: 44px;
    padding: 0 4px;
    border: none;
    background: none;
    color: inherit;
    font-size: 16px;
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
`;

const SpinnerOverlay = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.6);
    z-index: 2000;
`;

const StyledOutlineButton = styled.button.attrs({ type: "button" })`
    padding: 10px 18px;
    margin: 0.5rem 0.5rem;
    font-size: 18px;
    cursor: pointer;
    background-color: #fafafa;
    border: 1px solid var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    color: var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    &:hover {
        filter: brightness(0.8);
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
        filter: none;
        pointer-events: none;
    }
`;

const StyledButton = styled(StyledOutlineButton)`
    background-color: var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    border: 1px solid var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    color: #fff;
    margin-right: 0;
`;

export default Signature;
